import Navbar from '../components/Navbar';
import { colors, typography, components, presets, cn, Footer } from '../components/Constants';

export default function TermsOfService() {
  return (
    <>
      <Navbar />
      <div className={presets.pageWrapper}>
        <div className={presets.mainCard}>
          <h1 className={cn(typography.h1, colors.text.gradient)}>Terms of Service</h1>
          <p className={typography.small}>Last Updated: October 27, 2025</p>
          
          <p className={typography.bodyMb}>
            Welcome to KaydSystems. These Terms of Service ("Terms") govern your access to and use of our managed DevOps services. By engaging our services, you agree to these Terms.
          </p>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>1. Service Description</h2>
            <p className={cn(presets.bodyText, 'mb-3')}>
              KaydSystems provides fully managed DevOps services including but not limited to:
            </p>
            <ul className={presets.listItem}>
              <li>Application deployment and hosting</li>
              <li>DevOps consulting and infrastructure setup</li>
              <li>Security audits and compliance assistance</li>
              <li>24/7 monitoring and technical support</li>
              <li>CI/CD pipeline configuration and management</li>
              <li>Custom infrastructure design using Kubernetes, ArgoCD, and Terraform</li>
              <li>Application and software development</li>
              <li>Website hosting services</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>2. Account Registration</h2>
            <p className={cn(presets.bodyText, 'mb-3')}>To use our services, you must:</p>
            <ul className={presets.listItem}>
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Be at least 18 years old or the age of majority in your jurisdiction</li>
              <li>Have the authority to enter into these Terms on behalf of your organization</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>3. Service Agreements and Billing</h2>
            
            <h3 className={presets.subsectionTitle}>3.1 Service Plans</h3>
            <p className={cn(presets.bodyText, 'mb-3')}>
              Services are provided based on agreed-upon service plans detailed in your Service Order or Statement of Work (SOW). Each plan specifies:
            </p>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li>Scope of services</li>
              <li>Pricing and payment terms</li>
              <li>Service level commitments</li>
              <li>Support response times</li>
            </ul>
            
            <h3 className={presets.subsectionTitle}>3.2 Payment Terms</h3>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li>Fees are billed monthly in advance unless otherwise specified</li>
              <li>Payment is due within 15 days of invoice date</li>
              <li>Late payments may incur a fee of 1.5% per month or the maximum allowed by law</li>
              <li>All fees are non-refundable except as required by law</li>
              <li>Cloud infrastructure costs (AWS, GCP, Azure) are passed through at cost plus any agreed markup</li>
            </ul>
            
            <h3 className={presets.subsectionTitle}>3.3 Price Changes</h3>
            <p className={cn(presets.bodyText, 'mb-3')}>
              We may modify pricing with 30 days written notice. Continued use of services after the notice period constitutes acceptance of new pricing.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>4. Customer Responsibilities</h2>
            <p className={cn(presets.bodyText, 'mb-3')}>You are responsible for:</p>
            <ul className={presets.listItem}>
              <li>Providing timely access to systems, credentials, and information necessary for service delivery</li>
              <li>Ensuring your applications comply with all applicable laws and regulations</li>
              <li>Maintaining appropriate licenses for third-party software</li>
              <li>Backing up critical data independently of our services</li>
              <li>Notifying us promptly of any security incidents or concerns</li>
              <li>Compliance with our Acceptable Use Policy</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>5. Intellectual Property</h2>
            
            <h3 className={presets.subsectionTitle}>5.1 Customer Content</h3>
            <p className={cn(presets.bodyText, 'mb-4')}>
              You retain all rights to your applications, data, and content ("Customer Content"). You grant us a limited license to access, use, and modify Customer Content solely to provide our services.
            </p>
            
            <h3 className={presets.subsectionTitle}>5.2 KaydSystems Property</h3>
            <p className={cn(presets.bodyText, 'mb-3')}>
              We retain all rights to our platform, tools, methodologies, and any infrastructure configurations we develop. Upon termination, you receive:
            </p>
            <ul className={presets.listItem}>
              <li>Infrastructure-as-Code (Terraform) configurations specific to your environment</li>
              <li>Documentation of your infrastructure setup</li>
              <li>Kubernetes manifests and ArgoCD configurations</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>6. Confidentiality</h2>
            <p className={presets.bodyText}>
              Both parties agree to maintain the confidentiality of any proprietary or sensitive information disclosed during the service relationship. This obligation survives for 3 years after termination.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>7. Data Security and Privacy</h2>
            <p className={presets.bodyText}>
              We implement industry-standard security measures to protect your data. See our Privacy Policy and Data Processing Addendum for details on how we handle your information.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>8. Service Availability and Maintenance</h2>
            <ul className={presets.listItem}>
              <li>We strive to maintain high service availability as specified in our SLA</li>
              <li>Scheduled maintenance will be communicated at least 48 hours in advance when possible</li>
              <li>Emergency maintenance may be performed with minimal notice to protect system integrity</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>9. Limitation of Liability</h2>
            <div className={cn(components.alertBox('yellow'), 'mb-4')}>
              <p className={cn('font-semibold', colors.text.primary, 'mb-2')}>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
              <ul className={cn('list-disc list-inside space-y-2', colors.text.secondary, 'ml-4')}>
                <li>Our total liability shall not exceed the fees paid by you in the 12 months preceding the claim</li>
                <li>We are not liable for indirect, incidental, special, consequential, or punitive damages</li>
                <li>We are not liable for data loss if you failed to maintain independent backups</li>
                <li>We are not responsible for third-party service failures (cloud providers, ISPs, etc.)</li>
              </ul>
            </div>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>10. Warranties and Disclaimers</h2>
            <p className={presets.bodyText}>
              We warrant that services will be performed in a professional manner consistent with industry standards. EXCEPT FOR THIS WARRANTY, SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>11. Termination</h2>
            
            <h3 className={presets.subsectionTitle}>11.1 Termination by Customer</h3>
            <p className={cn(presets.bodyText, 'mb-4')}>
              You may terminate services with 30 days written notice. You remain responsible for fees through the end of the notice period.
            </p>
            
            <h3 className={presets.subsectionTitle}>11.2 Termination by KaydSystems</h3>
            <p className={cn(presets.bodyText, 'mb-3')}>We may terminate immediately if you:</p>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li>Breach these Terms or our Acceptable Use Policy</li>
              <li>Fail to pay undisputed invoices within 30 days</li>
              <li>Engage in fraudulent or illegal activities</li>
            </ul>
            
            <h3 className={presets.subsectionTitle}>11.3 Post-Termination</h3>
            <p className={cn(presets.bodyText, 'mb-3')}>Upon termination:</p>
            <ul className={presets.listItem}>
              <li>You have 30 days to retrieve your data</li>
              <li>We provide infrastructure documentation and configurations</li>
              <li>We may delete your data after 30 days</li>
              <li>You remain liable for all fees incurred before termination</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>12. Indemnification</h2>
            <p className={presets.bodyText}>
              You agree to indemnify and hold KaydSystems harmless from claims arising from your use of services, violation of these Terms, or infringement of third-party rights.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>13. Dispute Resolution</h2>
            <p className={cn(presets.bodyText, 'mb-3')}>Any disputes shall be resolved through:</p>
            <ol className={components.orderedList}>
              <li>Good faith negotiation between parties</li>
              <li>Mediation if negotiation fails</li>
              <li>Binding arbitration as a last resort</li>
            </ol>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>14. Governing Law</h2>
            <p className={presets.bodyText}>
              These Terms are governed by the laws of IA/USA, without regard to conflict of law provisions.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>15. Changes to Terms</h2>
            <p className={presets.bodyText}>
              We may modify these Terms with 30 days notice. Continued use after changes constitutes acceptance.
            </p>
          </section>
          
          <div className={cn(components.contactBox, 'mt-8')}>
            <h3 className={cn(typography.h4, colors.text.primary)}>Contact Information</h3>
            <p className={presets.bodyText}>Email: support@kaydsystems.com</p>
            <p className={presets.bodyText}>Address: 6701 Corporate Dr Ste N Johnston, IA 50131, United States</p>
          </div>
        </div>
        
        <Footer />
      </div>
    </>
  );
}